import useSWRInfinite from "swr/infinite";
import { mutate as globalMutate } from "swr";
import { useEffect, useCallback } from "react";
import { getLeadNotes, addLeadNote, editLeadNote } from "../services/notesService";

const DEFAULT_PAGE_SIZE = 10;

/**
 * Generate cache key for lead notes SWR
 */
export function getNotesCacheKey(leadId, page, pageSize, options = {}) {
  return [
    "notes",
    leadId ? String(leadId).toLowerCase() : "null",
    page,
    pageSize,
    options.isImportant ?? "all",
    options.search || ""
  ];
}

/**
 * Trigger refetch/invalidation for lead notes across all components
 * @param {string|number} leadId - The lead ID to invalidate
 */
export function invalidateNotesCache(leadId = null) {
  const targetId = leadId ? String(leadId).toLowerCase() : null;

  // Dispatch custom event to instantly trigger refetch in active useNotes hooks
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("refreshLeadNotes", { detail: { leadId: targetId } }));
  }

  // SWR globalMutate fallback
  return globalMutate(
    (key) => {
      if (!key) return false;
      if (typeof key === "string") {
        return key.includes("notes") && (!targetId || key.toLowerCase().includes(targetId));
      }
      if (Array.isArray(key) && key[0] === "notes") {
        if (!targetId) return true;
        const keyId = String(key[1] || "").toLowerCase();
        return keyId === targetId || keyId.includes(targetId) || targetId.includes(keyId);
      }
      return false;
    },
    undefined,
    { revalidate: true }
  );
}

// Alias for invalidateNotesCache as refetchNotes
export const refetchNotes = invalidateNotesCache;

/**
 * Custom hook to manage lead notes with SWR caching, pagination, and actions
 * @param {string|number} leadId - The lead ID
 * @param {Object} options - Filtering and pagination options
 * @returns {Object} - Hook state and helper functions
 */
export function useNotes(leadId = null, options = {}) {
  const pageSize = options.pageSize || DEFAULT_PAGE_SIZE;
  const isImportant = options.isImportant;
  const rawSearch = (options.search || "").trim();
  const search = rawSearch.length >= 3 ? rawSearch : "";

  const getKey = (pageIndex, previousPageData) => {
    if (!leadId) return null;

    // Stop fetching if last page returned no records
    if (previousPageData && (!previousPageData.data || previousPageData.data.length === 0)) {
      return null;
    }

    return getNotesCacheKey(leadId, pageIndex + 1, pageSize, { isImportant, search });
  };

  const { data, error, size, setSize, isValidating, isLoading, mutate } = useSWRInfinite(
    getKey,
    async ([, lId, page, sizeLimit, imp, srch]) => {
      if (!lId || lId === "null") return { data: [], totalRecords: 0 };
      const res = await getLeadNotes(lId, {
        pageNumber: page,
        pageSize: sizeLimit,
        sortOrder: "desc",
        isImportant: imp === "all" ? null : imp,
        search: srch,
      });

      if (res.success) {
        return res;
      }
      throw new Error(res.error || "Failed to fetch notes");
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 2000,
      keepPreviousData: true,
    }
  );

  const notes = data ? data.flatMap((page) => page.data || []) : [];
  const totalRecords = data?.[0]?.totalRecords || 0;

  const lastPage = data?.[data.length - 1];
  const hasMore = lastPage
    ? lastPage.data && lastPage.data.length > 0 && notes.length < totalRecords
    : false;

  const loadMore = () => {
    if (!isValidating && hasMore) {
      setSize((prev) => prev + 1);
    }
  };

  const refetch = useCallback(async () => {
    await setSize(1);
    return mutate();
  }, [setSize, mutate]);

  // Listen for global refreshLeadNotes event
  useEffect(() => {
    const handleRefreshEvent = (e) => {
      const targetId = e?.detail?.leadId;
      const currentLeadId = leadId ? String(leadId).toLowerCase() : null;
      if (
        !targetId ||
        !currentLeadId ||
        targetId === currentLeadId ||
        targetId.includes(currentLeadId) ||
        currentLeadId.includes(targetId)
      ) {
        refetch();
      }
    };

    if (typeof window !== "undefined") {
      window.addEventListener("refreshLeadNotes", handleRefreshEvent);
      return () => window.removeEventListener("refreshLeadNotes", handleRefreshEvent);
    }
  }, [leadId, refetch]);

  const createNote = async (noteDescription, isImp = false) => {
    const result = await addLeadNote(leadId, noteDescription, isImp);
    if (result.success) {
      await refetch();
      invalidateNotesCache(leadId);
    }
    return result;
  };

  const updateNote = async (noteId, noteDescription, isImp = false, status = "Active") => {
    const result = await editLeadNote(noteId, noteDescription, isImp, status);
    if (result.success) {
      await refetch();
      invalidateNotesCache(leadId);
    }
    return result;
  };

  const deleteNote = async (noteId) => {
    const { deleteLeadNote } = await import("../services/notesService");
    const result = await deleteLeadNote(noteId);
    if (result.success) {
      await refetch();
      invalidateNotesCache(leadId);
    }
    return result;
  };

  return {
    notes,
    totalRecords,
    isLoading: isLoading || (size > 1 && isValidating && notes.length === 0),
    isValidating,
    hasMore,
    error: error ? error.message || "Failed to load notes" : null,
    loadMore,
    refetch,
    refresh: refetch,
    mutate,
    createNote,
    updateNote,
    deleteNote,
  };
}

export default useNotes;
